/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// INTERFACE
import { ServerInterface } from "../../interfaces/Server.interface";
// CONSTANTS
import { BLANK_SERVER } from "../../constants/server";
import { FormAction } from "../../types/enums";

/**
 * State of the edge server slice: the edge server currently being viewed or edited,
 * the active selection, and the open/closed flags for this area's modals.
 */
export interface ServerState {
  server: ServerInterface;
  idServerSelected: string;
  nameServerSelected: string;
  openModalCreateNewServer: boolean;
  openModalEditServer: boolean;
  openModalDeleteServer: boolean;
  openModalUpdateServer: boolean;
  openModalExportServer: boolean;
  openDrawerServer: boolean;
  serverTableParams: any;
  serverRefreshTable: boolean;
  serverBanksRefreshTable: boolean;
  serverList: Array<any>;
  serverBreadcrumb: string[];
  serverUpdateBreadcrumb: any;
  openModalSecretClient: boolean;
  clearFilters: boolean;
  formAction: FormAction;
}

const initialState: ServerState = {
  server: { ...BLANK_SERVER },
  idServerSelected: "",
  nameServerSelected: "",
  openModalCreateNewServer: false,
  openModalEditServer: false,
  openModalDeleteServer: false,
  openModalUpdateServer: false,
  openModalExportServer: false,
  openDrawerServer: false,
  serverTableParams: {
    pagination: {
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "50", "100"],
    },
  },
  serverRefreshTable: false,
  serverBanksRefreshTable: false,
  serverList: [],
  serverBreadcrumb: [],
  serverUpdateBreadcrumb: {
    update: false,
    id: "",
    name: "",
  },
  openModalSecretClient: false,
  clearFilters: false,
  formAction: FormAction.Create,
};

export const serverSlice = createSlice({
  name: "server",
  initialState,
  reducers: {
    updateServer: (state, action: PayloadAction<ServerInterface>) => {
      state.server = action.payload;
    },
    updateIdServerSelected: (state, action: PayloadAction<string>) => {
      state.idServerSelected = action.payload;
    },
    updateNameServerSelected: (state, action: PayloadAction<string>) => {
      state.nameServerSelected = action.payload;
    },
    updateOpenModalCreateNewServer: (state, action: PayloadAction<boolean>) => {
      state.openModalCreateNewServer = action.payload;
    },
    updateOpenModalEditServer: (state, action: PayloadAction<boolean>) => {
      state.openModalEditServer = action.payload;
    },
    updateOpenModalDeleteServer: (state, action: PayloadAction<boolean>) => {
      state.openModalDeleteServer = action.payload;
    },
    updateOpenModalUpdateServer: (state, action: PayloadAction<boolean>) => {
      state.openModalUpdateServer = action.payload;
    },
    updateOpenModalExportServer: (state, action: PayloadAction<boolean>) => {
      state.openModalExportServer = action.payload;
    },
    updateOpenDrawerServer: (state, action: PayloadAction<boolean>) => {
      state.openDrawerServer = action.payload;
    },
    updateServerTableParams: (state, action: PayloadAction<any>) => {
      state.serverTableParams = action.payload;
    },
    updateServerRefreshTable: (state, action: PayloadAction<boolean>) => {
      state.serverRefreshTable = action.payload;
    },
    updateServerBanksRefreshTable: (state, action: PayloadAction<boolean>) => {
      state.serverBanksRefreshTable = action.payload;
    },
    updateServerList: (state, action: PayloadAction<any>) => {
      state.serverList = action.payload;
    },
    updateServerBreadcrumb: (state, action: PayloadAction<string[]>) => {
      state.serverBreadcrumb = action.payload;
    },
    updateServerUpdateBreadcrumb: (state, action: PayloadAction<any>) => {
      state.serverUpdateBreadcrumb = action.payload;
    },
    updateOpenModalSecretClient: (state, action: PayloadAction<boolean>) => {
      state.openModalSecretClient = action.payload;
    },
    updateClearFilters: (state, action: PayloadAction<boolean>) => {
      state.clearFilters = action.payload;
    },
    updateFormAction: (state, action: PayloadAction<FormAction>) => {
      state.formAction = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  updateServer,
  updateIdServerSelected,
  updateNameServerSelected,
  updateOpenModalCreateNewServer,
  updateOpenModalEditServer,
  updateOpenModalDeleteServer,
  updateOpenModalUpdateServer,
  updateOpenModalExportServer,
  updateOpenDrawerServer,
  updateServerTableParams,
  updateServerRefreshTable,
  updateServerBanksRefreshTable,
  updateServerList,
  updateServerBreadcrumb,
  updateServerUpdateBreadcrumb,
  updateOpenModalSecretClient,
  updateClearFilters,
  updateFormAction,
} = serverSlice.actions;

export default serverSlice.reducer;
